import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getLtvCohort, exportCsv } from 'services/api/AdebifyApi';
import commonSelectors from 'store/common/selectors';
import { commonActions } from 'store/common';
import settingsSelector from 'store/settings/selectors';
import types from './types';
import actions from './actions';
import { selectSettings } from './selectors';

function* fetchCohort() {
  try {
    yield put(actions.requestStart());

    const period = yield select(commonSelectors.periodSelector);
    const settings = yield select(selectSettings);
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;

    if (!period || !period.start || !period.end) {
      return;
    }

    // Public API contract (jug-api ltv_cohort handler / validator):
    //   start_date, end_date  → YYYY-MM-DD
    //   display_mode          → cumulative | per_elapsed_month
    //   metric                → ltv | total_ltv | cv_count | f2_conversion_rate
    // Cohort rows are always monthly (shop.ltv_cohort.f1_month).
    const params = {
      start_date: period.start.format('YYYY-MM-DD'),
      end_date: period.end.format('YYYY-MM-DD'),
      display_mode: settings.cumulativeMode,
      metric: settings.metric,
      axis: settings.axis,
      channel,
    };

    // settings.filters is already mapped to f1_* API keys by the container.
    // Skip the field altogether when no filter is active so the API keeps its
    // unfiltered default behaviour.
    if (settings.filters && Object.keys(settings.filters).length > 0) {
      params.filters = settings.filters;
    }

    const response = yield call(getLtvCohort, params);
    yield put(actions.fetchCohortSucceeded(response.data));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || error.message || 'データの取得に失敗しました';
    yield put(actions.requestFailed(errorMessage));
  }
}

function* handleDownloadCsv() {
  try {
    yield put(commonActions.setDownloadNotify('CSVファイルを作成しています...'));

    const period = yield select(commonSelectors.periodSelector);
    const settings = yield select(selectSettings);
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;

    const exportParams = {
      report_type: 'ltv_cohort',
      display_mode: settings.cumulativeMode,
      metric: settings.metric,
      axis: settings.axis,
      channel,
    };
    if (settings.filters && Object.keys(settings.filters).length > 0) {
      exportParams.filters = settings.filters;
    }
    if (period?.start && period?.end) {
      exportParams.start_date = period.start.format('YYYY-MM-DD');
      exportParams.end_date = period.end.format('YYYY-MM-DD');
    }

    const response = yield call(exportCsv, exportParams);

    // Response contains a presigned S3 download URL
    const downloadUrl = response.data?.download_url;
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    yield put(commonActions.setDownloadNotify('CSVファイルを作成しました'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('CSV export failed:', error);
    yield put(commonActions.setDownloadNotify(''));
  }
}

export default function* adebifyLtvCohortOperations() {
  yield takeLatest(types.FETCH_COHORT, fetchCohort);
  yield takeLatest(types.SET_CUMULATIVE_MODE, fetchCohort);
  yield takeLatest(types.SET_METRIC, fetchCohort);
  yield takeLatest(types.SET_AXIS, fetchCohort);
  yield takeLatest(types.SET_FILTERS, fetchCohort);
  yield takeLatest(types.DOWNLOAD_CSV, handleDownloadCsv);
}

// Exported for saga payload contract tests.
export { fetchCohort, handleDownloadCsv };
